import { Plus, X } from 'lucide-react'
import {
  makeDefaultOccurrence,
  occurrencesMissingTime,
} from '~/entities/event/lib/event-occurrences'
import * as m from '~/paraglide/messages'
import type { OccurrenceForm } from '~/shared/types/event'
import { DatePicker } from '~/shared/ui/date-picker'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Switch } from '~/shared/ui/switch'

interface EventOccurrencesEditorProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
}

export function EventOccurrencesEditor({ form }: EventOccurrencesEditorProps) {
  const validateOccurrences = ({ value }: { value: OccurrenceForm[] }) =>
    value.length > 0 && occurrencesMissingTime(value) ? m.everyDayNeedsTimeRange() : undefined

  return (
    <form.Field
      name="occurrences"
      validators={{ onChange: validateOccurrences, onBlur: validateOccurrences }}
    >
      {(field: {
        state: { value: OccurrenceForm[]; meta: { errors: (string | undefined)[] } }
      }) => {
        const occurrences = field.state.value ?? []
        const error = field.state.meta.errors[0]

        const setOccurrences = (next: OccurrenceForm[]) => form.setFieldValue('occurrences', next)
        const updateOccurrence = (index: number, patch: Partial<OccurrenceForm>) =>
          setOccurrences(occurrences.map((o, i) => (i === index ? { ...o, ...patch } : o)))
        const removeOccurrence = (index: number) =>
          setOccurrences(occurrences.filter((_, i) => i !== index))
        const addOccurrence = () =>
          setOccurrences([
            ...occurrences,
            makeDefaultOccurrence(occurrences[occurrences.length - 1]?.date),
          ])

        return (
          <div className="space-y-2" data-field="occurrences">
            <Label className="text-xs text-muted-foreground">{m.recurringDatesLabel()}</Label>
            <div className="space-y-2">
              {occurrences.map((occurrence, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and freely edited
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <div className="w-40">
                    <DatePicker
                      value={occurrence.date}
                      onChange={(date) => updateOccurrence(index, { date })}
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch
                      checked={occurrence.isAllDay}
                      onCheckedChange={(checked) =>
                        updateOccurrence(index, { isAllDay: !!checked })
                      }
                    />
                    <span className="text-xs text-muted-foreground">{m.allDay()}</span>
                  </div>
                  {!occurrence.isAllDay && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={occurrence.startTime}
                        className="bg-background w-30"
                        onChange={(e) => updateOccurrence(index, { startTime: e.target.value })}
                      />
                      <span className="text-muted-foreground text-xs">-</span>
                      <Input
                        type="time"
                        value={occurrence.endTime}
                        className="bg-background w-30"
                        onChange={(e) => updateOccurrence(index, { endTime: e.target.value })}
                      />
                      {occurrence.startTime &&
                        occurrence.endTime &&
                        occurrence.endTime <= occurrence.startTime && (
                          <span
                            className="whitespace-nowrap text-muted-foreground text-xs"
                            title={m.endsNextDayHint()}
                          >
                            {m.endsNextDay()}
                          </span>
                        )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeOccurrence(index)}
                    className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                    aria-label={m.removeDate()}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button
              type="button"
              onClick={addOccurrence}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="h-4 w-4" />
              {m.addDate()}
            </button>
          </div>
        )
      }}
    </form.Field>
  )
}
